import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ChevronUp, ChevronDown, Info } from 'lucide-react'

// Column definitions with tooltips
type SortKey =
  | 'name'
  | 'activeProjects'
  | 'totalUnits'
  | 'sellThroughPct'
  | 'medianSqmPrice'
  | 'medianSqmPricePercentile'
  | 'medianDaysOnMarket'
  | 'gmv'
  | 'pipelineValue'
  | 'salesLast90d'
  | 'freshness'

interface ColumnDef {
  key: SortKey
  label: string
  tooltip: string
  formula?: string
  align?: 'left' | 'right'
}

const COLUMNS: ColumnDef[] = [
  {
    key: 'name',
    label: 'Name',
    tooltip: 'Entity name (developer, broker, or architect)',
    align: 'left',
  },
  {
    key: 'activeProjects',
    label: 'Active projects',
    tooltip: 'Number of non-sold-out projects',
    formula: 'COUNT(projects) WHERE sold_out = false',
    align: 'right',
  },
  {
    key: 'totalUnits',
    label: 'Units',
    tooltip: 'Sold units / Total units across all projects',
    formula: 'sold_units / total_units',
    align: 'right',
  },
  {
    key: 'sellThroughPct',
    label: 'Sell-through',
    tooltip: 'Percentage of units that have been sold',
    formula: '(sold_units / total_units) × 100',
    align: 'right',
  },
  {
    key: 'medianSqmPrice',
    label: 'Median €/m²',
    tooltip: 'Middle price per square meter across all units (50th percentile)',
    formula: 'PERCENTILE_CONT(0.5) of sqm_price (outliers €500-€25k excluded)',
    align: 'right',
  },
  {
    key: 'medianSqmPricePercentile',
    label: 'Percentile',
    tooltip: 'How this entity\'s median €/m² ranks against all others. E.g., 24% means priced lower than 76% of peers.',
    formula: 'Position in sorted median_sqm_price list / total entities × 100',
    align: 'right',
  },
  {
    key: 'medianDaysOnMarket',
    label: 'Median DoM',
    tooltip: 'Median Days on Market — how long units typically take to sell',
    formula: 'PERCENTILE_CONT(0.5) of (sales_date - listing_date) for sold units',
    align: 'right',
  },
  {
    key: 'gmv',
    label: 'GMV',
    tooltip: 'Gross Merchandise Value — total revenue from sold units',
    formula: 'SUM(sale_price) from unit_history, fallback to current_price',
    align: 'right',
  },
  {
    key: 'pipelineValue',
    label: 'Pipeline',
    tooltip: 'Total value of unsold inventory (available + reserved units)',
    formula: 'SUM(current_price) WHERE status IN (available, reserved)',
    align: 'right',
  },
  {
    key: 'salesLast90d',
    label: 'Sales 90d',
    tooltip: 'Number of units sold in the last 90 days',
    formula: 'COUNT(units) WHERE sales_date > NOW() - 90 days',
    align: 'right',
  },
  {
    key: 'freshness',
    label: 'Freshness',
    tooltip: 'Days since last successful scrape. Red warning if > 14 days.',
    formula: 'NOW() - last_successful_scrape_at',
    align: 'right',
  },
]

export interface ScorecardRow {
  id: number
  name: string
  activeProjects: number
  totalUnits: number
  soldUnits: number
  sellThroughPct: number
  medianDaysOnMarket: number | null
  medianSqmPrice: number | null
  medianSqmPricePercentile: number | null
  gmv: number
  pipelineValue: number
  salesLast90d: number
  lastSuccessfulScrapeAt: string | null
}

export type ScorecardKind = 'developer' | 'broker' | 'architect'

interface Props {
  kind: ScorecardKind
  rows: ScorecardRow[] | undefined
  loading?: boolean
  error?: unknown
  /** When true, renders a leading checkbox column used by compare mode. */
  selectable?: boolean
  /** Entity ids currently selected (controlled; required when `selectable` is true). */
  selectedIds?: number[]
  /** Emits the updated selection when a checkbox flips. */
  onSelectionChange?: (ids: number[]) => void
  /** Max number of rows that can be selected at once (default 4). */
  maxSelection?: number
}

function euro(value: number | null | undefined): string {
  if (value == null) return '—'
  if (value >= 1_000_000_000) return `€${(value / 1_000_000_000).toFixed(2)}B`
  if (value >= 1_000_000) return `€${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `€${(value / 1_000).toFixed(0)}k`
  return `€${Math.round(value)}`
}

function pct(value: number | null | undefined, digits = 1): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${value.toFixed(digits)}%`
}

function daysSince(iso: string | null): number | null {
  if (!iso) return null
  const diff = Date.now() - new Date(iso).getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24))
}

const DETAIL_ROUTE: Record<ScorecardKind, string> = {
  developer: '/developers',
  broker: '/brokers',
  architect: '/architects',
}

type SortDirection = 'asc' | 'desc'
interface SortState {
  key: SortKey
  direction: SortDirection
}

// Tooltip component for column headers
function ColumnTooltip({ col }: { col: ColumnDef }) {
  return (
    <div className="absolute z-50 top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 bg-secondary-800 text-white text-xs rounded-lg shadow-lg w-64 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200">
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-[-1px] border-4 border-transparent border-b-secondary-800" />
      <div className="font-medium mb-1">{col.label}</div>
      <div className="text-secondary-300 mb-1">{col.tooltip}</div>
      {col.formula && (
        <div className="text-secondary-400 font-mono text-[10px] mt-1 border-t border-secondary-600 pt-1">
          {col.formula}
        </div>
      )}
    </div>
  )
}

// Sortable header component
function SortableHeader({
  col,
  sortState,
  onSort,
}: {
  col: ColumnDef
  sortState: SortState | null
  onSort: (key: SortKey) => void
}) {
  const isActive = sortState?.key === col.key
  const direction = isActive ? sortState.direction : null

  return (
    <th
      className={`py-2 pr-4 cursor-pointer select-none hover:bg-secondary-100 transition-colors relative group ${
        col.align === 'right' ? 'text-right' : 'text-left'
      }`}
      onClick={() => onSort(col.key)}
    >
      <div className={`inline-flex items-center gap-1 ${col.align === 'right' ? 'flex-row-reverse' : ''}`}>
        <span className="flex items-center gap-0.5">
          {col.label}
          <Info className="w-3 h-3 text-secondary-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        </span>
        <span className="w-4 flex-shrink-0">
          {isActive && direction === 'asc' && <ChevronUp className="w-4 h-4 text-primary-600" />}
          {isActive && direction === 'desc' && <ChevronDown className="w-4 h-4 text-primary-600" />}
          {!isActive && (
            <ChevronUp className="w-4 h-4 text-secondary-300 opacity-0 group-hover:opacity-50" />
          )}
        </span>
      </div>
      <ColumnTooltip col={col} />
    </th>
  )
}

export function ScorecardTable({
  kind,
  rows,
  loading,
  error,
  selectable = false,
  selectedIds,
  onSelectionChange,
  maxSelection = 4,
}: Props) {
  const [sortState, setSortState] = useState<SortState | null>({ key: 'gmv', direction: 'desc' })

  const handleSort = (key: SortKey) => {
    setSortState((prev) => {
      if (prev?.key === key) {
        // Toggle direction or clear
        if (prev.direction === 'desc') return { key, direction: 'asc' }
        if (prev.direction === 'asc') return null
      }
      // Default to descending for numeric, ascending for name
      return { key, direction: key === 'name' ? 'asc' : 'desc' }
    })
  }

  // Sort rows based on current sort state
  const sortedRows = useMemo(() => {
    if (!rows || !sortState) return rows
    const { key, direction } = sortState
    const multiplier = direction === 'asc' ? 1 : -1

    return [...rows].sort((a, b) => {
      let aVal: number | string | null
      let bVal: number | string | null

      // Special handling for computed/derived values
      if (key === 'freshness') {
        aVal = daysSince(a.lastSuccessfulScrapeAt)
        bVal = daysSince(b.lastSuccessfulScrapeAt)
      } else if (key === 'totalUnits') {
        // Sort by total units, not the ratio
        aVal = a.totalUnits
        bVal = b.totalUnits
      } else {
        aVal = a[key as keyof ScorecardRow] as number | string | null
        bVal = b[key as keyof ScorecardRow] as number | string | null
      }

      // Handle nulls (push to end)
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return 1
      if (bVal == null) return -1

      // String comparison for name
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return multiplier * aVal.localeCompare(bVal)
      }

      // Numeric comparison
      return multiplier * ((aVal as number) - (bVal as number))
    })
  }, [rows, sortState])

  if (error) {
    return <div className="card text-sm text-red-600">Failed to load scorecards.</div>
  }
  if (loading || !rows) {
    return <div className="card h-[360px] animate-pulse bg-secondary-100 rounded" />
  }
  if (rows.length === 0) {
    return <div className="card text-sm text-secondary-500">No entities match the current filters.</div>
  }

  const selectedSet = new Set(selectedIds ?? [])
  const atMax = selectedSet.size >= maxSelection

  const toggle = (id: number) => {
    if (!onSelectionChange) return
    const next = new Set(selectedSet)
    if (next.has(id)) {
      next.delete(id)
    } else {
      if (next.size >= maxSelection) return
      next.add(id)
    }
    onSelectionChange(Array.from(next))
  }

  const displayRows = sortedRows ?? rows

  return (
    <div className="card overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 z-10 bg-white">
          <tr className="text-left text-xs uppercase tracking-wide text-secondary-500 border-b">
            {selectable && <th className="py-2 pr-2 w-8" aria-label="Select" />}
            <th className="py-2 pr-4">#</th>
            {COLUMNS.map((col) => (
              <SortableHeader key={col.key} col={col} sortState={sortState} onSort={handleSort} />
            ))}
          </tr>
        </thead>
        <tbody>
          {displayRows.map((r, idx) => {
            const stale = daysSince(r.lastSuccessfulScrapeAt)
            const staleBadge = stale != null && stale > 14
            const checked = selectedSet.has(r.id)
            const disabled = selectable && !checked && atMax
            return (
              <tr key={r.id} className="border-b last:border-0 hover:bg-secondary-50">
                {selectable && (
                  <td className="py-2 pr-2 w-8">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggle(r.id)}
                      className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500 disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label={`Select ${r.name}`}
                    />
                  </td>
                )}
                <td className="py-2 pr-4 text-secondary-500">{idx + 1}</td>
                <td className="py-2 pr-4 font-medium">
                  <Link to={`${DETAIL_ROUTE[kind]}/${r.id}`} className="text-primary-600 hover:underline">
                    {r.name}
                  </Link>
                </td>
                <td className="py-2 pr-4 text-right">{r.activeProjects}</td>
                <td className="py-2 pr-4 text-right">
                  {r.soldUnits}/{r.totalUnits}
                </td>
                <td className="py-2 pr-4 text-right">{pct(r.sellThroughPct)}</td>
                <td className="py-2 pr-4 text-right">{euro(r.medianSqmPrice)}</td>
                <td className="py-2 pr-4 text-right">{pct(r.medianSqmPricePercentile, 0)}</td>
                <td className="py-2 pr-4 text-right">
                  {r.medianDaysOnMarket == null ? '—' : `${Math.round(r.medianDaysOnMarket)}d`}
                </td>
                <td className="py-2 pr-4 text-right">{euro(r.gmv)}</td>
                <td className="py-2 pr-4 text-right">{euro(r.pipelineValue)}</td>
                <td className="py-2 pr-4 text-right">{r.salesLast90d}</td>
                <td className="py-2 pr-4 text-right">
                  {stale == null ? (
                    <span className="text-secondary-400">—</span>
                  ) : staleBadge ? (
                    <span className="inline-flex items-center gap-1 text-red-600">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      {stale}d
                    </span>
                  ) : (
                    <span className="text-secondary-500">{stale}d</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
